// DIMSTYLE / HEADER / DSTYLE helpers for DXF import sizing.
// dxf-parser skips the DIMSTYLE table; these scanners read it from the raw DXF string.

export interface DXFDimStyle {
  name: string;
  dimScale?: number;
  dimAsz?: number;
  dimTxt?: number;
}

export interface DXFDimHeaderDefaults {
  dimStyleName?: string;
  dimScale?: number;
  dimAsz?: number;
  dimTxt?: number;
}

export interface DXFDimStyleOverrides {
  dimScale?: number;
  dimAsz?: number;
  dimTxt?: number;
}

export interface DStyleScanState {
  inDStyle: boolean;
  pendingGroup?: number;
  overrides: DXFDimStyleOverrides;
}

export function createDStyleScanState(): DStyleScanState {
  return { inDStyle: false, overrides: {} };
}

/** Feeds one DXF group into a DSTYLE scanner. Returns true if the group was consumed. */
export function feedDStyleGroup(state: DStyleScanState, code: number, value: string | number): boolean {
  if (code === 1000 && value === "DSTYLE") {
    // 1000: xdata string — DSTYLE block start
    state.inDStyle = true;
    return true;
  }
  if (!state.inDStyle) {
    return false;
  }
  if (code === 1002) {
    // 1002: xdata control ("{" / "}")
    if (value === "}") {
      state.inDStyle = false;
      state.pendingGroup = undefined;
    }
    return true;
  }
  if (code === 1070) {
    // 1070: xdata int — pending DIMSTYLE group (40/41/140)
    state.pendingGroup = typeof value === "number" ? value : parseInt(String(value), 10);
    return true;
  }
  if (code === 1040 && state.pendingGroup !== undefined) {
    // 1040: xdata float — pending value
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (state.pendingGroup === 40) {
      // DIMSCALE
      state.overrides.dimScale = n;
    } else if (state.pendingGroup === 41) {
      // DIMASZ
      state.overrides.dimAsz = n;
    } else if (state.pendingGroup === 140) {
      // DIMTXT
      state.overrides.dimTxt = n;
    }
    state.pendingGroup = undefined;
    return true;
  }
  // Non-DSTYLE group while open (malformed / unclosed) — stop consuming so layer/color/etc. still apply.
  // Callers exit their entity loop on code 0 before feeding it here; this is the real unclosed-block guard.
  state.inDStyle = false;
  state.pendingGroup = undefined;
  return false;
}

export function dstyleScanResult(state: DStyleScanState): DXFDimStyleOverrides | undefined {
  const { dimScale, dimAsz, dimTxt } = state.overrides;
  if (dimScale === undefined && dimAsz === undefined && dimTxt === undefined) {
    return undefined;
  }
  return state.overrides;
}

export function parseDimStyles(dxf: string): { [name: string]: DXFDimStyle } {
  const styles: { [name: string]: DXFDimStyle } = {};
  const lines = dxf.split(/\r?\n/);

  let inDimStyleTable = false;
  let current: Partial<DXFDimStyle> | null = null;

  const commit = () => {
    if (current?.name) {
      styles[current.name] = current as DXFDimStyle;
    }
    current = null;
  };

  for (let i = 0; i < lines.length - 1; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1]?.trim();

    if (code === "0" && value === "TABLE") {
      // 0: entity/table type
      const nextCode = lines[i + 2]?.trim();
      const nextValue = lines[i + 3]?.trim();
      if (nextCode === "2" && nextValue === "DIMSTYLE") {
        // 2: table name
        inDimStyleTable = true;
      }
      continue;
    }

    if (!inDimStyleTable) {
      continue;
    }

    if (code === "0" && value === "ENDTAB") {
      // 0: end of table
      commit();
      break;
    }

    if (code === "0" && value === "DIMSTYLE") {
      // 0: start of DIMSTYLE record
      commit();
      current = {};
      continue;
    }

    if (!current) {
      continue;
    }

    if (code === "2") {
      // 2: style name
      current.name = value;
    } else if (code === "40") {
      // 40: DIMSCALE
      current.dimScale = parseFloat(value);
    } else if (code === "41") {
      // 41: DIMASZ
      current.dimAsz = parseFloat(value);
    } else if (code === "140") {
      // 140: DIMTXT
      current.dimTxt = parseFloat(value);
    }
  }

  // Flush last entry if ENDTAB is missing (truncated / malformed table).
  commit();
  return styles;
}

export function parseDimHeaderDefaults(dxf: string): DXFDimHeaderDefaults {
  const defaults: DXFDimHeaderDefaults = {};
  const lines = dxf.split(/\r?\n/);

  for (let i = 0; i < lines.length - 1; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1]?.trim();

    // Break on the first ENDSEC — safe because HEADER is always the first DXF section per spec.
    if (code === "0" && value === "ENDSEC") {
      // 0: end of HEADER section
      break;
    }

    if (code !== "9") {
      // 9: HEADER variable name
      continue;
    }

    const valueCode = lines[i + 2]?.trim();
    const valueValue = lines[i + 3]?.trim();
    if (!valueCode || valueValue === undefined) {
      continue;
    }

    if (value === "$DIMSTYLE" && valueCode === "2") {
      // 2: string — dim style name
      defaults.dimStyleName = valueValue;
    } else if (value === "$DIMSCALE" && valueCode === "40") {
      // 40: float — DIMSCALE
      defaults.dimScale = parseFloat(valueValue);
    } else if (value === "$DIMASZ" && valueCode === "40") {
      // 40: float — DIMASZ
      defaults.dimAsz = parseFloat(valueValue);
    } else if (value === "$DIMTXT" && valueCode === "40") {
      // 40: float — DIMTXT
      defaults.dimTxt = parseFloat(valueValue);
    }
  }

  return defaults;
}

export function findDimStyle(styles: { [name: string]: DXFDimStyle }, name: string): DXFDimStyle | undefined {
  if (styles[name]) {
    return styles[name];
  }
  const lower = name.toLowerCase();
  for (const key of Object.keys(styles)) {
    if (key.toLowerCase() === lower) {
      return styles[key];
    }
  }
  return undefined;
}

/**
 * Resolve DIMASZ/DIMTXT onto Higharc STANDARD-path sizes.
 * DSTYLE size var → drawing size; DSTYLE scale-only → styleVar × that scale;
 * else paper (DIMSCALE > 1) ratio vs Standard (or model size if no Standard);
 * DIMSCALE 0 uses the style var as-is; otherwise fall back to expectedStandardSize.
 */
export function resolveNormalizedDimSize(props: {
  dimStyles: { [name: string]: DXFDimStyle };
  dimHeaderDefaults: DXFDimHeaderDefaults;
  styleName: string | undefined;
  varKey: "dimAsz" | "dimTxt";
  fallbackExpectedSize: number;
  dstyleOverrides?: DXFDimStyleOverrides;
}): number | undefined {
  const { dimStyles, dimHeaderDefaults, styleName, varKey, fallbackExpectedSize, dstyleOverrides } = props;

  const resolvedName = styleName ?? dimHeaderDefaults.dimStyleName;
  const style = resolvedName ? findDimStyle(dimStyles, resolvedName) : undefined;
  const standard = findDimStyle(dimStyles, "Standard");
  const styleScale = style?.dimScale ?? dimHeaderDefaults.dimScale;

  const styleVar = dstyleOverrides?.[varKey] ?? style?.[varKey] ?? dimHeaderDefaults[varKey];
  if (styleVar === undefined || !(styleVar > 0)) {
    return undefined;
  }

  // Entity DSTYLE: size and/or scale overrides → AutoCAD drawing size (value × scale).
  const dstyleScale = dstyleOverrides?.dimScale;
  if (dstyleOverrides?.[varKey] !== undefined || dstyleScale !== undefined) {
    const scale = dstyleScale ?? styleScale ?? 1;
    return scale > 0 ? styleVar * scale : styleVar;
  }

  if (styleScale !== undefined && styleScale > 1) {
    // HEADER fills missing Standard fields only when a Standard row exists — never as a stand-in.
    const standardVar = standard ? standard[varKey] ?? dimHeaderDefaults[varKey] : undefined;
    if (standardVar !== undefined && standardVar > 0 && styleVar === standardVar) {
      return fallbackExpectedSize;
    }
    return styleVar * styleScale;
  }

  if (styleScale === 0) {
    return styleVar;
  }

  // DIMSCALE 1 / missing / (0, 1]: treat as STANDARD-aligned — use Higharc expected size, not styleVar.
  // Classic paper styles use DIMSCALE > 1 (e.g. 48, 96); fractional scales in (0, 1] are not handled as paper.
  return fallbackExpectedSize;
}
